"use client";

import { useEffect, useState, useMemo } from "react";
import { fetchWithAuth } from "@/lib/auth";
import { TourButton } from "@/components/tours/TourButton";
import { useAutoTour } from "@/components/tours/useAutoTour";
import { TOURS } from "@/components/tours/catalog";

interface Assignment { assignment_id: string; user_id: string; role: string; entity_type: string; entity_id: number; entity_name: string; }
interface Meeting { id: string; status: string; entity_type: string; entity_id: number; start_at?: string; meeting_format?: string; }
interface GroupMeta { id: number; type: string; name: string; status: string; volunteer_count: number; }

// ─── Chart primitives ─────────────────────────────────────────────────────────

function DonutChart({ segments, size = 120 }: {
    segments: { label: string; value: number; color: string }[];
    size?: number;
}) {
    const total = segments.reduce((s, x) => s + x.value, 0);
    if (total === 0) return <div className="w-[120px] h-[120px] rounded-full bg-[#f0f0f0] flex items-center justify-center text-xs text-[#999]">No data</div>;

    let cumulative = 0;
    const r = 46; const cx = 60; const cy = 60;
    const arcs = segments.map(seg => {
        const pct = seg.value / total;
        const start = cumulative;
        cumulative += pct;
        const startAngle = start * 2 * Math.PI - Math.PI / 2;
        const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
        const x1 = cx + r * Math.cos(startAngle);
        const y1 = cy + r * Math.sin(startAngle);
        const x2 = cx + r * Math.cos(endAngle);
        const y2 = cy + r * Math.sin(endAngle);
        const large = pct > 0.5 ? 1 : 0;
        return { ...seg, d: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`, pct };
    });

    return (
        <svg width={size} height={size} viewBox="0 0 120 120">
            {arcs.map((a, i) => <path key={i} d={a.d} fill={a.color} stroke="white" strokeWidth="2" />)}
            <circle cx={cx} cy={cy} r={28} fill="white" />
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize="14" fontWeight="900" fill="#003d45">{total}</text>
            <text x={cx} y={cy + 12} textAnchor="middle" fontSize="7" fontWeight="700" fill="#999" letterSpacing="1">TOTAL</text>
        </svg>
    );
}

function BarChart({ bars }: {
    bars: { label: string; value: number; color: string }[];
}) {
    const max = Math.max(...bars.map(b => b.value), 1);
    return (
        <div className="space-y-2.5">
            {bars.map((b, i) => (
                <div key={i} className="flex items-center gap-3">
                    <span className="text-[10px] font-black uppercase text-[#666] w-28 flex-shrink-0 text-right truncate">{b.label}</span>
                    <div className="flex-1 h-6 bg-[#f0f0f0] rounded-full overflow-hidden">
                        <div
                            className="h-full rounded-full transition-all duration-700 flex items-center justify-end pr-2"
                            style={{ width: `${(b.value / max) * 100}%`, background: b.color, minWidth: b.value > 0 ? "24px" : "0" }}
                        >
                            {b.value > 0 && <span className="text-[9px] font-black text-white">{b.value}</span>}
                        </div>
                    </div>
                    <span className="text-xs font-black text-[#003d45] w-8 text-right">{b.value}</span>
                </div>
            ))}
        </div>
    );
}

function KpiCard({ label, value, icon, accent, sub }: { label: string; value: number | string; icon: string; accent: string; sub?: string }) {
    return (
        <div className="bg-white rounded-2xl p-5 border border-[#e2e8f0] shadow-sm flex flex-col gap-2 hover:shadow-md transition-shadow">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
                <span className="material-symbols-outlined text-xl">{icon}</span>
            </div>
            <p className="text-3xl font-black text-[#003d45] leading-none">{value}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#666]">{label}</p>
            {sub && <p className="text-[11px] text-[#aaa]">{sub}</p>}
        </div>
    );
}

function HeroBadge({ label, value, icon, cls }: { label: string; value: number | string; icon: string; cls: string }) {
    return (
        <div className={`rounded-2xl p-5 flex items-center gap-4 ${cls}`}>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-2xl">{icon}</span>
            </div>
            <div>
                <p className="text-3xl font-black leading-none">{value}</p>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mt-1">{label}</p>
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function AnalyticsPanel() {
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [meetings, setMeetings] = useState<Meeting[]>([]);
    const [groups, setGroups] = useState<GroupMeta[]>([]);
    const [loading, setLoading] = useState(true);
    const { replay: replayTour } = useAutoTour("staff.analytics", TOURS["staff.analytics"]);

    useEffect(() => {
        async function load() {
            try {
                const [aRes, mRes, gRes] = await Promise.all([
                    fetchWithAuth("/api/v1/organizations/assignments?limit=2000"),
                    fetchWithAuth("/api/v1/meetings"),
                    fetchWithAuth("/api/v1/organizations/all-groups?include_inactive=false"),
                ]);
                if (aRes.ok) setAssignments(await aRes.json());
                if (mRes.ok) setMeetings(await mRes.json());
                if (gRes.ok) { const d = await gRes.json(); setGroups(Array.isArray(d) ? d : []); }
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    const stats = useMemo(() => {
        const uniqueVolunteers = new Set(assignments.map(a => a.user_id)).size;
        const uniqueGroups = new Set(assignments.map(a => `${a.entity_type}-${a.entity_id}`)).size;

        // Role distribution
        const roleCounts: Record<string, number> = {};
        assignments.forEach(a => { roleCounts[a.role] = (roleCounts[a.role] || 0) + 1; });

        // Groups by type (from full groups list)
        const typeCounts: Record<string, number> = {};
        groups.forEach(g => { typeCounts[g.type] = (typeCounts[g.type] || 0) + 1; });

        // Meeting status
        const meetingStatusCounts: Record<string, number> = {};
        meetings.forEach(m => { meetingStatusCounts[m.status] = (meetingStatusCounts[m.status] || 0) + 1; });

        // Chair / Vice Chair coverage
        const groupsWithChair = new Set(assignments.filter(a => a.role === "Chair").map(a => `${a.entity_type}-${a.entity_id}`)).size;
        const groupsWithVC = new Set(assignments.filter(a => a.role === "Vice Chair").map(a => `${a.entity_type}-${a.entity_id}`)).size;
        const totalGroups = groups.length || uniqueGroups;
        const chairCoveragePct = totalGroups > 0 ? Math.round((groupsWithChair / totalGroups) * 100) : 0;
        const vcCoveragePct = totalGroups > 0 ? Math.round((groupsWithVC / totalGroups) * 100) : 0;

        // Pending / upcoming
        const pendingApprovals = meetings.filter(m => ["pending_review", "pending_staff_review"].includes(m.status)).length;
        const now = new Date();
        const upcomingEvents = meetings.filter(m => m.start_at && new Date(m.start_at) > now && m.status === "approved").length;

        // Top 8 roles
        const topRoles = Object.entries(roleCounts).sort(([, a], [, b]) => b - a).slice(0, 8);

        // Groups missing Chair
        const groupsWithChairSet = new Set(assignments.filter(a => a.role === "Chair").map(a => `${a.entity_type}-${a.entity_id}`));
        const groupsMissingChair = groups
            .filter(g => !groupsWithChairSet.has(`${g.type}-${g.id}`))
            .sort((a, b) => (b.volunteer_count || 0) - (a.volunteer_count || 0))
            .slice(0, 8);

        // Groups missing Staff Liaison (committees only — Staff Liaisons don't apply to chapters or communities)
        const groupsWithSLSet = new Set(assignments.filter(a => a.role === "Staff Liaison").map(a => `${a.entity_type}-${a.entity_id}`));
        const groupsMissingStaffLiaison = groups.filter(g => g.type === "committee" && !groupsWithSLSet.has(`${g.type}-${g.id}`)).length;

        // Top groups by volunteer count
        const volunteerCountByGroup: Record<string, { name: string; type: string; count: number }> = {};
        assignments.forEach(a => {
            const k = `${a.entity_type}-${a.entity_id}`;
            if (!volunteerCountByGroup[k]) volunteerCountByGroup[k] = { name: a.entity_name, type: a.entity_type, count: 0 };
            volunteerCountByGroup[k].count++;
        });
        const topGroups = Object.values(volunteerCountByGroup).sort((a, b) => b.count - a.count).slice(0, 6);

        // Volunteer engagement score (rough)
        const avgVolunteersPerGroup = totalGroups > 0 ? Math.round((assignments.length / totalGroups) * 10) / 10 : 0;

        return {
            uniqueVolunteers, uniqueGroups: totalGroups, groupsWithChair, groupsWithVC,
            chairCoveragePct, vcCoveragePct, roleCounts, typeCounts, meetingStatusCounts,
            topRoles, pendingApprovals, upcomingEvents, groupsMissingChair,
            groupsMissingStaffLiaison, topGroups, avgVolunteersPerGroup,
        };
    }, [assignments, meetings, groups]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-[#e2e8f0] border-t-[#003d45] rounded-full animate-spin" />
            </div>
        );
    }

    const typeColors: Record<string, string> = { committee: "#003d45", chapter: "#007a8a", community: "#ae232d" };
    const statusColors: Record<string, string> = {
        approved: "#10b981", completed: "#003d45", pending_review: "#f59e0b",
        pending_staff_review: "#f97316", draft: "#94a3b8", live: "#6366f1", submitted: "#a78bfa",
    };

    const TYPE_BADGE: Record<string, string> = {
        committee: "bg-[#003d45] text-white",
        chapter: "bg-[#007a8a] text-white",
        community: "bg-[#ae232d] text-white",
    };

    const donutTypeSegments = Object.entries(stats.typeCounts).map(([k, v]) => ({
        label: k, value: v, color: typeColors[k] || "#888",
    }));
    const donutStatusSegments = Object.entries(stats.meetingStatusCounts).map(([k, v]) => ({
        label: k, value: v, color: statusColors[k] || "#ccc",
    }));

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-black text-[#003d45] mb-1">Staff Overview</h2>
                    <p className="text-sm text-[#666]">Real-time snapshot of the volunteer ecosystem.</p>
                </div>
                <TourButton onClick={replayTour} />
            </div>

            {/* KPI Grid */}
            <div id="analytics-kpis" className="grid grid-cols-2 xl:grid-cols-5 gap-4">
                <KpiCard label="Active Volunteers" value={stats.uniqueVolunteers} icon="person" accent="bg-[#003d45]/10 text-[#003d45]" sub={`${stats.avgVolunteersPerGroup} avg per group`} />
                <KpiCard label="Total Groups" value={stats.uniqueGroups} icon="group_work" accent="bg-[#007a8a]/10 text-[#007a8a]" />
                <KpiCard label="Pending Approvals" value={stats.pendingApprovals} icon="pending_actions" accent="bg-amber-100 text-amber-700" />
                <KpiCard label="Upcoming Events" value={stats.upcomingEvents} icon="event" accent="bg-emerald-100 text-emerald-700" />
                <KpiCard label="Total Activities" value={meetings.length} icon="bolt" accent="bg-purple-100 text-purple-700" />
            </div>

            {/* Hero coverage badges */}
            <div id="analytics-coverage" className="grid grid-cols-2 gap-4">
                <HeroBadge label="Groups with Chair" value={stats.groupsWithChair} icon="verified" cls="bg-emerald-600 text-white" />
                <HeroBadge label="Groups with Vice Chair" value={stats.groupsWithVC} icon="supervised_user_circle" cls="bg-[#007a8a] text-white" />
            </div>

            {/* Coverage gauges + donuts */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* Chair coverage gauge */}
                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-5 shadow-sm flex flex-col gap-3">
                    <h3 className="font-black text-[#003d45] text-xs uppercase tracking-widest">Chair Coverage</h3>
                    <div className="flex flex-col items-center justify-center flex-1 gap-2">
                        <svg viewBox="0 0 140 80" className="w-40">
                            <path d="M 10 70 A 60 60 0 0 1 130 70" fill="none" stroke="#f0f0f0" strokeWidth="12" strokeLinecap="round" />
                            <path d="M 10 70 A 60 60 0 0 1 130 70" fill="none"
                                stroke={stats.chairCoveragePct > 70 ? "#10b981" : stats.chairCoveragePct > 40 ? "#f59e0b" : "#ae232d"}
                                strokeWidth="12" strokeLinecap="round"
                                strokeDasharray={`${(stats.chairCoveragePct / 100) * 188} 188`} />
                            <text x="70" y="66" textAnchor="middle" fontSize="22" fontWeight="900" fill="#003d45">{stats.chairCoveragePct}%</text>
                            <text x="70" y="78" textAnchor="middle" fontSize="7" fontWeight="700" fill="#999" letterSpacing="1">COVERAGE</text>
                        </svg>
                        <p className="text-xs text-[#666] text-center">{stats.groupsWithChair} of {stats.uniqueGroups} groups have a Chair</p>
                        {stats.uniqueGroups - stats.groupsWithChair > 0 && (
                            <p className="text-[11px] text-[#ae232d] font-bold">{stats.uniqueGroups - stats.groupsWithChair} missing Chair</p>
                        )}
                    </div>
                </div>

                {/* Groups by type donut */}
                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-5 shadow-sm flex flex-col gap-4">
                    <h3 className="font-black text-[#003d45] text-xs uppercase tracking-widest">Groups by Type</h3>
                    <div className="flex items-center gap-5 flex-1">
                        <DonutChart segments={donutTypeSegments} size={110} />
                        <div className="space-y-2.5 flex-1">
                            {donutTypeSegments.map(s => (
                                <div key={s.label} className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
                                    <span className="text-xs text-[#666] capitalize flex-1">{s.label}</span>
                                    <span className="text-xs font-black text-[#003d45]">{s.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Activity status donut */}
                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-5 shadow-sm flex flex-col gap-4">
                    <h3 className="font-black text-[#003d45] text-xs uppercase tracking-widest">Activity Status</h3>
                    <div className="flex items-center gap-5 flex-1">
                        <DonutChart segments={donutStatusSegments} size={110} />
                        <div className="space-y-2 flex-1">
                            {donutStatusSegments.map(s => (
                                <div key={s.label} className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
                                    <span className="text-[10px] text-[#666] capitalize flex-1 truncate">{s.label.replace(/_/g, " ")}</span>
                                    <span className="text-xs font-black text-[#003d45]">{s.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Role distribution */}
            <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-5">
                    <span className="material-symbols-outlined text-[#003d45] text-xl">assignment_ind</span>
                    <h3 className="font-black text-[#003d45] uppercase tracking-wider text-sm">Role Distribution</h3>
                    <span className="ml-auto text-[10px] text-[#999] font-bold">{assignments.length} total assignments</span>
                </div>
                <BarChart
                    bars={stats.topRoles.map(([role, count]) => ({
                        label: role,
                        value: count,
                        color: role === "Chair" ? "#003d45" : role === "Vice Chair" ? "#007a8a" : role === "Staff Liaison" ? "#f59e0b" : role === "Board Liaison" ? "#6366f1" : "#94a3b8",
                    }))}
                />
            </div>

            {/* Leadership gaps + Top groups */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Groups missing Chair */}
                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="material-symbols-outlined text-[#ae232d] text-xl">warning</span>
                        <h3 className="font-black text-[#003d45] uppercase tracking-wider text-sm">Leadership Gaps</h3>
                        <span className="ml-auto px-2 py-0.5 bg-red-50 text-[#ae232d] text-[10px] font-black rounded-full border border-red-100">
                            {stats.uniqueGroups - stats.groupsWithChair} without Chair
                        </span>
                    </div>
                    <div className="space-y-2">
                        {stats.groupsMissingChair.length === 0 ? (
                            <div className="flex items-center gap-2 py-4 text-emerald-600">
                                <span className="material-symbols-outlined">check_circle</span>
                                <span className="text-sm font-bold">All groups have a Chair!</span>
                            </div>
                        ) : stats.groupsMissingChair.map(g => (
                            <div key={g.id} className="flex items-center gap-3 py-2 border-b border-[#f5f5f5] last:border-0">
                                <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full flex-shrink-0 ${TYPE_BADGE[g.type] || "bg-slate-200 text-slate-600"}`}>{g.type}</span>
                                <span className="text-sm text-[#333] flex-1 truncate">{g.name}</span>
                                <span className="text-[10px] text-[#999] flex-shrink-0 font-bold">{g.volunteer_count} vol.</span>
                                <span className="w-2 h-2 rounded-full bg-[#ae232d] flex-shrink-0" title="No Chair" />
                            </div>
                        ))}
                        {stats.uniqueGroups - stats.groupsWithChair > 8 && (
                            <p className="text-[10px] text-[#999] pt-1 text-center">
                                +{stats.uniqueGroups - stats.groupsWithChair - 8} more groups missing a Chair
                            </p>
                        )}
                    </div>

                    {stats.groupsMissingStaffLiaison > 0 && (
                        <div className="mt-4 pt-4 border-t border-[#f0f0f0] flex items-center gap-2">
                            <span className="material-symbols-outlined text-amber-500 text-base">info</span>
                            <span className="text-[11px] text-[#666]">
                                <strong className="text-amber-600">{stats.groupsMissingStaffLiaison}</strong> groups also lack a Staff Liaison
                            </span>
                        </div>
                    )}
                </div>

                {/* Top groups by volunteer count */}
                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="material-symbols-outlined text-[#007a8a] text-xl">leaderboard</span>
                        <h3 className="font-black text-[#003d45] uppercase tracking-wider text-sm">Top Groups by Volunteers</h3>
                    </div>
                    <div className="space-y-2.5">
                        {stats.topGroups.map((g, i) => {
                            const max = stats.topGroups[0]?.count || 1;
                            return (
                                <div key={g.name} className="flex items-center gap-3">
                                    <span className="text-[10px] font-black text-[#ccc] w-4 flex-shrink-0 text-right">{i + 1}</span>
                                    <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full flex-shrink-0 ${TYPE_BADGE[g.type] || "bg-slate-200 text-slate-600"}`}>{g.type[0].toUpperCase()}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2 mb-0.5">
                                            <span className="text-xs text-[#333] truncate font-medium">{g.name}</span>
                                            <span className="text-xs font-black text-[#003d45] flex-shrink-0">{g.count}</span>
                                        </div>
                                        <div className="h-1.5 bg-[#f0f0f0] rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all duration-700"
                                                style={{ width: `${(g.count / max) * 100}%`, background: typeColors[g.type] || "#888" }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Activity breakdown bars */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-5">
                        <span className="material-symbols-outlined text-[#003d45] text-xl">corporate_fare</span>
                        <h3 className="font-black text-[#003d45] uppercase tracking-wider text-sm">Groups by Type</h3>
                    </div>
                    <BarChart bars={Object.entries(stats.typeCounts).map(([t, v]) => ({ label: t, value: v, color: typeColors[t] || "#888" }))} />
                </div>

                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-5">
                        <span className="material-symbols-outlined text-[#003d45] text-xl">event_note</span>
                        <h3 className="font-black text-[#003d45] uppercase tracking-wider text-sm">Activities by Status</h3>
                    </div>
                    <BarChart bars={Object.entries(stats.meetingStatusCounts).map(([s, v]) => ({
                        label: s.replace(/_/g, " "),
                        value: v,
                        color: statusColors[s] || "#ccc",
                    }))} />
                </div>
            </div>
        </div>
    );
}
